import { useState, useEffect, useMemo } from "react";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DiscountField } from "../form-fields/DiscountField";
import { SurchargeField } from "../form-fields/SurchargeField";
import { DescriptionField } from "../form-fields/DescriptionField";
import { CategorySelector } from "./CategorySelector";
import { CategoryManager } from "./CategoryManager";
import { PaymentMethodSelector } from "./PaymentMethodSelector";
import { PaymentMethodManager } from "./PaymentMethodManager";
import { usePaymentCategories } from "@/hooks/payments/usePaymentCategories";
import { usePaymentMethods } from "@/hooks/payments/usePaymentMethods";
import { DatePicker } from "@/components/ui/date-picker";
import { supabase } from "@/integrations/supabase/client";
import { supportsGroupCategories } from "@/utils/vikingosFeatures";
import { Search, X, Users } from "lucide-react";

interface PaymentFormContentProps {
  gymId: string | null;
  /** Id of the member who is paying — used to exclude them from extra-beneficiary search. */
  payerId?: string | null;
  onSubmit: (data: {
    amount: number;
    discount: number;
    description: string;
    validity_days: number;
    paid_at: string;
    payment_method: string;
    covered_user_ids: string[];
  }) => Promise<void>;
  onCancel?: () => void;
}

interface BeneficiaryOption {
  id: string;
  name: string;
  source: "profile" | "pending";
}

export const PaymentFormContent = ({ gymId, payerId, onSubmit, onCancel }: PaymentFormContentProps) => {
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [isManaging, setIsManaging] = useState(false);
  const [isManagingMethods, setIsManagingMethods] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [surchargePercent, setSurchargePercent] = useState(0);
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paidAt, setPaidAt] = useState<string>(format(new Date(), "yyyy-MM-dd"));
  const [manualAmount, setManualAmount] = useState<number>(0);
  const [manualValidityDays, setManualValidityDays] = useState<number>(30);
  const [selectedMethodId, setSelectedMethodId] = useState<string | null>(null);
  const [coveredUserIds, setCoveredUserIds] = useState<string[]>([]);
  const [beneficiarySearch, setBeneficiarySearch] = useState("");
  const [activeSlot, setActiveSlot] = useState<number | null>(null);

  const { categories, isLoading } = usePaymentCategories(gymId);
  const { methods, isLoading: methodsLoading } = usePaymentMethods(gymId);

  // Gym name for feature gating (Vikingos only).
  const { data: gymName } = useQuery({
    queryKey: ["gym-name", gymId],
    queryFn: async () => {
      if (!gymId) return null;
      const { data } = await supabase.from("gyms").select("name").eq("id", gymId).maybeSingle();
      return data?.name || null;
    },
    enabled: !!gymId,
  });
  const groupCategoriesEnabled = supportsGroupCategories(gymName);

  // Members for beneficiary search.
  const { data: members } = useQuery({
    queryKey: ["gym-members-for-beneficiary", gymId],
    queryFn: async (): Promise<BeneficiaryOption[]> => {
      if (!gymId) return [];
      const [profilesRes, pendingRes] = await Promise.all([
        supabase.from("profiles").select("id, first_name, last_name, username").eq("gym_id", gymId),
        supabase.from("pending_members").select("id, first_name, last_name, dni").eq("gym_id", gymId),
      ]);
      const profiles = (profilesRes.data || []).map((p) => ({
        id: p.id,
        name:
          [p.first_name, p.last_name].filter(Boolean).join(" ").trim() ||
          p.username?.split("@")[0] ||
          "Sin nombre",
        source: "profile" as const,
      }));
      const pending = (pendingRes.data || [])
        .filter((p: any) => p.dni !== "__WALKIN__")
        .map((p) => ({
          id: p.id,
          name: [p.first_name, p.last_name].filter(Boolean).join(" ").trim() || "Sin nombre",
          source: "pending" as const,
        }));
      return [...profiles, ...pending].sort((a, b) => a.name.localeCompare(b.name));
    },
    enabled: !!gymId && groupCategoriesEnabled,
  });

  // Auto-select first method when loaded
  useEffect(() => {
    if (!selectedMethodId && methods.length > 0) {
      setSelectedMethodId(methods[0].id);
    }
  }, [methods, selectedMethodId]);

  const selectedCategories = categories.filter((cat) =>
    selectedCategoryIds.includes(cat.id)
  );

  const usingManual = selectedCategories.length === 0 && manualAmount > 0;

  const subtotalAmount = usingManual
    ? manualAmount
    : selectedCategories.reduce((sum, cat) => sum + cat.amount, 0);
  const surchargeAmount = Math.round(subtotalAmount * (surchargePercent / 100));
  const totalAmount = subtotalAmount + surchargeAmount;
  const maxValidityDays = usingManual
    ? manualValidityDays
    : selectedCategories.length > 0
      ? Math.max(...selectedCategories.map((cat) => cat.validity_days))
      : 30;

  // Group-pay logic: compute extra beneficiary slots needed.
  const maxCovers = groupCategoriesEnabled
    ? Math.max(1, ...selectedCategories.map((c) => c.covers_people || 1))
    : 1;
  const extraSlots = Math.max(0, maxCovers - 1);

  // Reset covered ids when slots change.
  useEffect(() => {
    setCoveredUserIds((prev) => prev.slice(0, extraSlots));
  }, [extraSlots]);

  const baseAutoDescription = usingManual
    ? "Pago manual"
    : selectedCategories.map((cat) => cat.name).join(", ");
  const autoDescription = surchargePercent > 0 && baseAutoDescription
    ? `${baseAutoDescription} (Recargo ${surchargePercent}%)`
    : baseAutoDescription;

  const selectedMethod = methods.find((m) => m.id === selectedMethodId);

  const beneficiaryNameById = useMemo(() => {
    const map = new Map<string, string>();
    (members || []).forEach((m) => map.set(m.id, m.name));
    return map;
  }, [members]);

  const filteredBeneficiaries = useMemo(() => {
    if (!members) return [];
    const q = beneficiarySearch.toLowerCase().trim();
    const exclude = new Set<string>(coveredUserIds);
    if (payerId) exclude.add(payerId);
    const base = members.filter((m) => !exclude.has(m.id));
    if (!q) return base.slice(0, 20);
    return base.filter((m) => m.name.toLowerCase().includes(q)).slice(0, 20);
  }, [members, beneficiarySearch, coveredUserIds, payerId]);

  const allSlotsFilled = coveredUserIds.length === extraSlots && coveredUserIds.every(Boolean);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (totalAmount <= 0) return;
    if (!selectedMethod) return;
    if (extraSlots > 0 && !allSlotsFilled) return;

    setIsSubmitting(true);
    try {
      await onSubmit({
        amount: totalAmount,
        discount,
        description: description || autoDescription,
        validity_days: maxValidityDays,
        paid_at: paidAt,
        payment_method: selectedMethod.name,
        covered_user_ids: coveredUserIds,
      });
      // Reset form
      setSelectedCategoryIds([]);
      setDiscount(0);
      setSurchargePercent(0);
      setDescription("");
      setPaidAt(format(new Date(), "yyyy-MM-dd"));
      setManualAmount(0);
      setManualValidityDays(30);
      setCoveredUserIds([]);
      setBeneficiarySearch("");
      setActiveSlot(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isManaging && gymId) {
    return <CategoryManager gymId={gymId} onBack={() => setIsManaging(false)} />;
  }

  if (isManagingMethods && gymId) {
    return <PaymentMethodManager gymId={gymId} onBack={() => setIsManagingMethods(false)} />;
  }

  const hasCategoriesSelected = selectedCategoryIds.length > 0;

  return (
    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
      <CategorySelector
        categories={categories}
        selectedIds={selectedCategoryIds}
        onSelectionChange={setSelectedCategoryIds}
        onManageClick={() => setIsManaging(true)}
        isLoading={isLoading}
      />

      <div className="relative flex items-center py-1">
        <div className="flex-grow border-t border-border" />
        <span className="mx-3 text-xs uppercase tracking-wider text-muted-foreground">o</span>
        <div className="flex-grow border-t border-border" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <label htmlFor="manual_amount" className="text-sm font-medium">
            Monto manual
          </label>
          <Input
            id="manual_amount"
            type="number"
            min="0"
            step="0.01"
            placeholder="0"
            value={manualAmount === 0 ? "" : manualAmount}
            onChange={(e) => setManualAmount(Number(e.target.value) || 0)}
            disabled={hasCategoriesSelected}
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="manual_validity" className="text-sm font-medium">
            Días de validez
          </label>
          <Input
            id="manual_validity"
            type="number"
            min="1"
            placeholder="30"
            value={manualValidityDays}
            onChange={(e) => setManualValidityDays(Number(e.target.value) || 1)}
            disabled={hasCategoriesSelected}
          />
        </div>
      </div>
      {hasCategoriesSelected && (
        <p className="text-xs text-muted-foreground -mt-2">
          Deseleccioná todas las categorías para usar un monto manual.
        </p>
      )}

      {/* Beneficiarios adicionales (solo Vikingos + categorías grupales) */}
      {extraSlots > 0 && (
        <div className="space-y-3 rounded-md border border-primary/30 bg-primary/5 p-3">
          <div className="flex items-center gap-2 text-sm font-medium text-primary">
            <Users className="h-4 w-4" />
            Beneficiarios adicionales ({extraSlots})
          </div>
          <p className="text-xs text-muted-foreground">
            Esta categoría cubre {maxCovers} personas. Elegí los socios que también
            quedarán al día con este pago. Solo se cobra una vez.
          </p>

          {Array.from({ length: extraSlots }).map((_, idx) => {
            const id = coveredUserIds[idx];
            if (id) {
              return (
                <div
                  key={idx}
                  className="flex items-center justify-between rounded-md border bg-background px-3 py-2 text-sm"
                >
                  <span>
                    <span className="text-muted-foreground mr-2">#{idx + 1}</span>
                    {beneficiaryNameById.get(id) || "Socio"}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => {
                      setCoveredUserIds((prev) => prev.filter((_, i) => i !== idx));
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              );
            }
            const isActive = activeSlot === idx;
            return (
              <div key={idx} className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground">
                  Beneficiario #{idx + 1}
                </label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    className="pl-8"
                    placeholder="Buscar socio..."
                    value={isActive ? beneficiarySearch : ""}
                    onFocus={() => {
                      setActiveSlot(idx);
                      setBeneficiarySearch("");
                    }}
                    onChange={(e) => {
                      setActiveSlot(idx);
                      setBeneficiarySearch(e.target.value);
                    }}
                  />
                </div>
                {isActive && beneficiarySearch && (
                  <div className="border rounded-md max-h-40 overflow-y-auto bg-background">
                    {filteredBeneficiaries.length === 0 ? (
                      <p className="p-2 text-xs text-muted-foreground">Sin resultados</p>
                    ) : (
                      filteredBeneficiaries.map((m) => (
                        <button
                          key={`${m.source}-${m.id}`}
                          type="button"
                          onClick={() => {
                            setCoveredUserIds((prev) => {
                              const next = [...prev];
                              next[idx] = m.id;
                              return next;
                            });
                            setBeneficiarySearch("");
                            setActiveSlot(null);
                          }}
                          className="w-full text-left p-2 text-sm hover:bg-muted transition-colors"
                        >
                          {m.name}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="space-y-2">
        <label className="text-sm font-medium">Fecha de pago</label>
        <DatePicker
          value={paidAt}
          onChange={setPaidAt}
          maxYear={new Date().getFullYear()}
          placeholder="Seleccionar fecha"
        />
      </div>

      <DiscountField value={discount} onChange={setDiscount} />

      <SurchargeField value={surchargePercent} onChange={setSurchargePercent} />

      {surchargePercent > 0 && subtotalAmount > 0 && (
        <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground space-y-0.5">
          <div className="flex justify-between"><span>Subtotal</span><span>${subtotalAmount.toLocaleString()}</span></div>
          <div className="flex justify-between"><span>Recargo {surchargePercent}%</span><span>+${surchargeAmount.toLocaleString()}</span></div>
          <div className="flex justify-between font-semibold text-foreground"><span>Total</span><span>${totalAmount.toLocaleString()}</span></div>
        </div>
      )}

      <DescriptionField
        value={description}
        onChange={setDescription}
        placeholder={autoDescription || "Descripción opcional..."}
      />

      <PaymentMethodSelector
        methods={methods}
        selectedId={selectedMethodId}
        onSelect={setSelectedMethodId}
        onManageClick={() => setIsManagingMethods(true)}
        isLoading={methodsLoading}
      />

      <Button
        type="submit"
        className="w-full"
        disabled={
          totalAmount <= 0 ||
          isSubmitting ||
          !selectedMethod ||
          (extraSlots > 0 && !allSlotsFilled)
        }
      >
        {isSubmitting
          ? "Registrando..."
          : extraSlots > 0 && !allSlotsFilled
            ? `Elegí ${extraSlots - coveredUserIds.length} socio(s) más`
            : "Registrar pago"}
      </Button>
      {onCancel && (
        <Button type="button" variant="outline" className="w-full" onClick={onCancel}>
          Cancelar
        </Button>
      )}
    </form>
  );
};
